import { supabase } from '../supabaseClient';

/**
 * 플레이어 이미지를 업로드합니다.
 * @param {string} playerName - 선수명
 * @param {string} team - 팀명
 * @param {File} imageFile - 업로드할 이미지 파일
 * @param {string} userId - 업로더의 사용자 ID (선택사항)
 * @returns {Promise<Object>} 업로드된 이미지 정보 { id, image_url, player_id, created_at }
 */
export const uploadPlayerImage = async (playerName, team, imageFile, userId = null) => {
  try {
    // 입력값 검증
    if (!playerName || !imageFile) {
      throw new Error('선수명과 이미지 파일은 필수입니다.');
    }

    // 파일 검증
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('이미지 파일만 업로드 가능합니다.');
    }

    // 스토리지 경로 생성 (timestamp-filename)
    const timestamp = Date.now();
    const filename = `${timestamp}-${imageFile.name || 'image.png'}`;
    const storagePath = filename;

    // 1. Supabase Storage에 이미지 업로드
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('player-faces')
      .upload(storagePath, imageFile, {
        cacheControl: '2592000',
        upsert: false,
      });

    if (uploadError) {
      console.error('이미지 업로드 중 오류:', uploadError);
      throw new Error(`이미지 업로드 실패: ${uploadError.message}`);
    }

    // 2. 공개 URL 생성
    const { data: publicUrlData } = supabase.storage
      .from('player-faces')
      .getPublicUrl(storagePath);

    const imageUrl = publicUrlData.publicUrl;

    // 3. 현재 로그인한 사용자 정보 조회 (userId가 없는 경우)
    let uploaderId = userId;
    if (!uploaderId) {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.user?.id) {
        console.warn('사용자 인증 정보 없음');
        uploaderId = null;
      } else {
        uploaderId = sessionData.session.user.id;
      }
    }

    // 4. player_images 테이블에 레코드 삽입
    const playerId = `${playerName}-${team}`;
    const { data: imageData, error: dbError } = await supabase
      .from('player_images')
      .insert({
        player_id: playerId,
        image_url: imageUrl,
        uploader_id: uploaderId,
        usage_count: 0,
        report_count: 0,
        is_blocked: false,
      })
      .select();

    if (dbError) {
      // 스토리지에는 업로드되었으므로 정리
      try {
        await supabase.storage
          .from('player-faces')
          .remove([storagePath]);
      } catch (storageError) {
        console.error('스토리지 정리 실패:', storageError);
        // 스토리지 정리 실패는 로깅만 하고 계속 진행 (고아 파일이 생길 수 있음)
      }

      console.error('데이터베이스 저장 중 오류:', dbError);
      throw new Error('이미지 저장 중 오류가 발생했습니다.');
    }

    // 응답 데이터 검증
    if (!imageData || !Array.isArray(imageData) || imageData.length === 0) {
      // 스토리지 정리
      try {
        await supabase.storage
          .from('player-faces')
          .remove([storagePath]);
      } catch (storageError) {
        console.error('스토리지 정리 실패:', storageError);
        // 스토리지 정리 실패는 로깅만 하고 계속 진행
      }

      throw new Error('이미지 저장 응답이 유효하지 않습니다.');
    }

    // 5. 성공 응답
    const savedImage = imageData[0];
    if (!savedImage.id || !savedImage.created_at) {
      throw new Error('저장된 이미지 정보가 불완전합니다.');
    }

    return {
      id: savedImage.id,
      image_url: imageUrl,
      player_id: playerId,
      created_at: savedImage.created_at,
    };
  } catch (error) {
    console.error('이미지 업로드 실패:', error);
    throw error;
  }
};

/**
 * 플레이어의 모든 이미지를 조회합니다.
 * @param {string} playerName - 선수명
 * @param {string} team - 팀명
 * @returns {Promise<Array>} 이미지 목록
 */
export const getPlayerImages = async (playerName, team) => {
  try {
    if (!playerName || !team) {
      throw new Error('선수명과 팀명은 필수입니다.');
    }

    const playerId = `${playerName}-${team}`;
    const { data, error } = await supabase
      .from('player_images')
      .select('id, image_url, uploader_id')
      .eq('player_id', playerId)
      .eq('is_blocked', false)
      .order('usage_count', { ascending: false });

    if (error) {
      console.error('이미지 조회 중 오류:', error);
      throw error;
    }

    // 응답 데이터 검증
    if (!Array.isArray(data)) {
      console.error('예상치 못한 응답 형식:', data);
      return [];
    }

    return data;
  } catch (error) {
    console.error('플레이어 이미지 조회 실패:', error);
    throw error;
  }
};

/**
 * 이미지를 신고합니다.
 * @param {string} imageId - 이미지 ID
 * @param {string} userId - 신고자의 사용자 ID (선택사항)
 * @returns {Promise<Object>} 신고 결과
 */
export const reportPlayerImage = async (imageId, userId = null) => {
  try {
    if (!imageId) {
      throw new Error('이미지 ID는 필수입니다.');
    }

    // 현재 로그인한 사용자 정보 조회 (userId가 없는 경우)
    let reporterId = userId;
    if (!reporterId) {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.user?.id) {
        console.warn('사용자 인증 정보 없음');
        reporterId = null;
      } else {
        reporterId = sessionData.session.user.id;
      }
    }

    // RPC 함수 호출
    const { data, error } = await supabase.rpc('report_image', {
      _image_id: imageId,
      _reporter_id: reporterId
    });

    if (error) {
      console.error('신고 중 오류:', error);
      throw error;
    }

    return {
      success: true,
      message: data
    };
  } catch (error) {
    console.error('이미지 신고 실패:', error);
    throw error;
  }
};

/**
 * 이미지 사용 횟수를 증가합니다.
 * @param {string} imageId - 이미지 ID
 * @returns {Promise<Object>} 업데이트 결과
 */
export const incrementImageUsage = async (imageId) => {
  try {
    if (!imageId) {
      throw new Error('이미지 ID는 필수입니다.');
    }

    const { error } = await supabase.rpc('increment_usage', {
      _image_id: imageId
    });

    if (error) {
      console.error('사용 횟수 업데이트 중 오류:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('이미지 사용 횟수 증가 실패:', error);
    throw error;
  }
};

/**
 * 이미지를 삭제합니다 (업로더만 가능, RPC 함수 사용).
 * @param {string} imageId - 이미지 ID
 * @returns {Promise<Object>} 삭제 결과
 */
export const deletePlayerImage = async (imageId) => {
  try {
    if (!imageId) {
      throw new Error('이미지 ID는 필수입니다.');
    }

    // RPC 함수 호출
    const { data, error } = await supabase.rpc('delete_my_image', {
      image_id_param: imageId
    });

    if (error) {
      console.error('이미지 삭제 중 오류:', error);
      throw new Error('이미지 삭제에 실패했습니다.');
    }

    // RPC 함수 응답 확인
    if (!data?.success) {
      const errorMessage = data?.message || '이미지 삭제에 실패했습니다.';
      throw new Error(errorMessage);
    }

    return { success: true, message: data.message };
  } catch (error) {
    console.error('이미지 삭제 실패:', error);
    throw error;
  }
};
